import { Platform } from "react-native"

import { PersistedClient, Persister } from "@tanstack/react-query-persist-client"

// The persisted React Query cache holds per-sponsor / per-role responses
// (member, role/info, etc.). With sponsorId per-tab, two tabs in different
// sponsors must NOT share a persisted cache or one tab's restoration would
// shove the other tab's stale data into its in-memory cache on cold boot.
// Routing this writer to `sessionScopedStorage` makes the persisted cache
// per-tab on web (sessionStorage) and a separate MMKV instance on native.
import { sessionScopedStorage as storage } from "./storage/mmkv"

function isQuotaExceededError(e: unknown): boolean {
  if (typeof DOMException !== "undefined" && e instanceof DOMException) {
    return e.name === "QuotaExceededError" || e.code === 22
  }
  return (
    e != null &&
    typeof e === "object" &&
    ((e as { name?: string }).name === "QuotaExceededError" ||
      (e as { message?: string }).message?.includes("quota") === true)
  )
}

export function createPersister(key: string): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        storage.set(key, JSON.stringify(client))
      } catch (e) {
        if (!isQuotaExceededError(e) || Platform.OS !== "web") throw e
        // On quota exceeded, evict the stale cache and skip this write rather
        // than crashing. The in-memory query cache is still fully functional;
        // the next app launch just won't have a warm cache.
        storage.delete(key)
        console.warn("tanstackQueryPersister: localStorage quota exceeded, cleared persisted cache")
      }
    },
    restoreClient: async () => {
      const client = storage.getString(key)
      if (client) {
        return JSON.parse(client) as PersistedClient
      }
      return undefined
    },
    removeClient: async () => {
      storage.delete(key)
    },
  }
}
